import styled from 'styled-components'
import { GOOGLE_CALENDAR_SUPPORTED_TYPE_NAME } from '../../constants'
import { useAuthWindow, usePreviewMode, useSetting } from '../../hooks'
import { useGetCalendars } from '../../services/api/events.hooks'
import { useIsFeatureEnabled } from '../../services/api/feature-flags.hooks'
import { useDeleteLinkedAccount, useGetLinkedAccounts, useGetSupportedTypes } from '../../services/api/settings.hooks'
import { Border, Colors, Spacing } from '../../styles'
import { pointerQuery } from '../../styles/dimensions'
import { icons, logos } from '../../styles/images'
import { TCalendar, TCalendarAccount } from '../../utils/types'
import Flex from '../atoms/Flex'
import GTSwitch from '../atoms/GTSwitch'
import { Icon } from '../atoms/Icon'
import GTButton from '../atoms/buttons/GTButton'
import { BodyLarge, BodyMedium, BodySmall } from '../atoms/typography/Typography'
import GTDropdownMenu from '../radix/GTDropdownMenu'
import { getCalendarAuthButton, getCalendarName } from './utils/utils'

const Calendar = styled(Flex)`
    cursor: pointer;
    border: ${Border.stroke.small} solid transparent;
    border-radius: ${Border.radius.small};
    padding: ${Spacing._8} ${Spacing._4};
    box-sizing: border-box;
    /* See ItemContainer: a stuck :hover after a tap reads as a selected row, and these rows carry a
       real selection. Hover is also the only thing marking them as selectable, so without it show
       the border at rest and move the background to the press state. */
    ${pointerQuery.canHover} {
        :hover {
            border: ${Border.stroke.small} solid ${Colors.background.border};
            background-color: ${Colors.background.base};
        }
    }
    ${pointerQuery.noHover} {
        border: ${Border.stroke.small} solid ${Colors.background.border};
        :active {
            background-color: ${Colors.background.base};
        }
    }
`

const TimeInput = styled.input`
    min-height: 40px;
    padding: ${Spacing._8};
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    border-radius: ${Border.radius.medium};
    background: ${Colors.background.white};
`

const AutoBlockSettings = () => {
    const enabled = useIsFeatureEnabled('calendar_planning')
    const { field_value: start, updateSetting: setStart } = useSetting('calendar_plan_start', enabled)
    const { field_value: end, updateSetting: setEnd } = useSetting('calendar_plan_end', enabled)
    const { field_value: morning, updateSetting: setMorning } = useSetting('calendar_plan_morning', enabled)
    if (!enabled) return null

    return (
        <Flex column gap={Spacing._12} data-testid="auto-block-settings">
            <Flex column gap={Spacing._4}>
                <BodyLarge>Auto-block</BodyLarge>
                <BodySmall>Hours and calendar for proposed task blocks</BodySmall>
            </Flex>
            <Flex gap={Spacing._12} alignItems="center">
                <label>
                    <BodySmall>Start</BodySmall>
                    <TimeInput
                        type="time"
                        step={900}
                        value={start || '09:00'}
                        onChange={(event) => setStart(event.target.value)}
                    />
                </label>
                <label>
                    <BodySmall>End</BodySmall>
                    <TimeInput
                        type="time"
                        step={900}
                        value={end || '17:30'}
                        onChange={(event) => setEnd(event.target.value)}
                    />
                </label>
            </Flex>
            <Flex justifyContent="space-between" alignItems="center">
                <Flex column gap={Spacing._4}>
                    <BodyMedium>Auto-block every morning</BodyMedium>
                    <BodySmall>Prepare a proposal on your first Overview visit</BodySmall>
                </Flex>
                <GTSwitch
                    label="Auto-block every morning"
                    checked={morning === 'true'}
                    onChange={(checked) => setMorning(checked ? 'true' : 'false')}
                />
            </Flex>
        </Flex>
    )
}

const CalendarSettings = () => {
    const { isPreviewMode } = usePreviewMode()
    const { updateSetting: setHasDismissedMulticalPrompt } = useSetting('has_dismissed_multical_prompt')
    const { data: calendars } = useGetCalendars()
    const { field_value: taskToCalAccount, updateSetting: setTaskToCalAccount } = useSetting(
        'calendar_account_id_for_new_tasks'
    )
    const { field_value: taskToCalCalendar, updateSetting: setTaskToCalCalendar } = useSetting(
        'calendar_calendar_id_for_new_tasks'
    )
    const { openAuthWindow } = useAuthWindow()
    const { data: linkedAccounts } = useGetLinkedAccounts()
    const { mutate: deleteAccount } = useDeleteLinkedAccount()
    const { data: supportedTypes } = useGetSupportedTypes()

    const handleTaskToCalSelect = (account: TCalendarAccount, calendar: TCalendar) => {
        setTaskToCalAccount(account.account_id)
        setTaskToCalCalendar(calendar.calendar_id)
    }
    const handleReauthorization = () => {
        const authUrl = supportedTypes?.find(
            (supportedType) => supportedType.name === GOOGLE_CALENDAR_SUPPORTED_TYPE_NAME
        )?.authorization_url
        if (authUrl) {
            openAuthWindow({ url: authUrl, isGoogleSignIn: true })
        }
    }
    const handleDeleteAccount = (accountId: string) => {
        const linkedAccountId = linkedAccounts
            ?.filter((linkedAccount) => linkedAccount.name === GOOGLE_CALENDAR_SUPPORTED_TYPE_NAME)
            .find((linkedAccount) => linkedAccount.display_id === accountId)?.id
        if (linkedAccountId) {
            deleteAccount({ id: linkedAccountId })
        }
    }

    return (
        <Flex gap={Spacing._24} column>
            <AutoBlockSettings />
            <Flex column gap={Spacing._4}>
                <BodyLarge>Choose default calendar</BodyLarge>
                <BodySmall>Choose the default calendar to create new events in</BodySmall>
            </Flex>

            {calendars?.map((account) => (
                <Flex column gap={Spacing._8} key={account.account_id}>
                    <Flex alignItems="center" justifyContent="space-between">
                        <Flex alignItems="center" gap={Spacing._8}>
                            <Icon icon={logos.gcal} />
                            <BodyMedium>{account.account_id}</BodyMedium>
                        </Flex>
                        <Flex gap={Spacing._8}>
                            {getCalendarAuthButton(account, handleReauthorization)}
                            <GTDropdownMenu
                                hideCheckmark
                                items={[
                                    {
                                        label: 'Disconnect account',
                                        onClick: () => handleDeleteAccount(account.account_id),
                                    },
                                ]}
                                trigger={
                                    <GTButton
                                        icon={icons.ellipsisVertical}
                                        tooltipText="More options"
                                        styleType="icon"
                                    />
                                }
                            />
                        </Flex>
                    </Flex>
                    <div>
                        {account.calendars
                            .filter((calendar) => calendar.can_write)
                            .sort((a, b) => {
                                // place the primary calendar at the top
                                if (a.calendar_id === 'primary' || a.calendar_id === account.account_id) return -1
                                if (b.calendar_id === 'primary' || b.calendar_id === account.account_id) return 1
                                return 0
                            })
                            .map((calendar) => (
                                <Calendar
                                    key={calendar.calendar_id}
                                    gap={Spacing._8}
                                    alignItems="center"
                                    onClick={() => handleTaskToCalSelect(account, calendar)}
                                >
                                    <Icon
                                        icon={icons.check}
                                        hidden={
                                            !(
                                                account.account_id === taskToCalAccount &&
                                                calendar.calendar_id === taskToCalCalendar
                                            )
                                        }
                                    />
                                    <Icon icon={icons.square} colorHex={calendar.color_background} />
                                    <BodyMedium>{getCalendarName(account.account_id, calendar.title)}</BodyMedium>
                                </Calendar>
                            ))}
                    </div>
                </Flex>
            ))}
            {isPreviewMode && (
                <Flex justifyContent="space-between" alignItems="center">
                    <BodyMedium>
                        Secret button to un-dismiss calendar auth banner (if you have calendars that need multi-cal
                        enabled)
                    </BodyMedium>
                    <GTButton
                        value="Un-dismiss"
                        styleType="secondary"
                        onClick={() => setHasDismissedMulticalPrompt('false')}
                    />
                </Flex>
            )}
        </Flex>
    )
}

export default CalendarSettings
